import { Tab, Tabs, Typography } from '@material-ui/core';
import { DebentureApiResponse, EmissaoStatus, GrupoAcesso } from 'common/genericTypes';
import Chip from 'components/chip';
import { HorizontalAppBar } from 'components/horizontalAppBar';
import SuspensaoHistorico, { SuspensaoHistoricoKind } from 'components/suspensaoHistorico';
import { LgSpace } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { RootState } from 'store';
import { DetalhesCaracteristicas } from './caracteristicas';
import { DetalhesCriteriosRemuneracao } from './criteriosRemuneracao';
import { DetalhesDocumentos } from './documentos';
import { DetalhesEventosFinanceiros } from 'common/detalhes/eventosFinanceiros';
import { initialDebenture } from './initialDebenture';
import Liquidacoes from './liquidacoes';
import Historico from './historico';
import KRIsOperacao from 'common/detalhes/krisOperacao';
import { DetalhesAditamento } from 'pages/instrumentos/detalhes/aditamentos';
import { DivergenciasButton } from 'components/divergenciasButton';

type ComponentParams = { debentureId: string };
type ComponentState = { initialTab?: number };

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const DetalhesDebenture: React.FC<Props> = (props) => {
  const { debentureId } = props.match.params;

  const [tabValue, setTabValue] = useState<number>(props.location.state?.initialTab ?? 0);
  const [detalhes, setDetalhes] = useState<DebentureApiResponse>(initialDebenture);
  const [loadedDetalhes, setLoadedDetalhes] = useState<Boolean>(false);

  const dispatch = useDispatch();
  const isUsuarioControlesInternos =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.ControlesInternos;
  const isUsuarioOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes;
  const getDebentureDetalhesApi = useSelector((state: RootState) => state.debentureApi.getDebentureDetalhes);

  const getDetalhes = useCallback(async () => {
    try {
      const result = await getDebentureDetalhesApi(dispatch, debentureId, true);
      setDetalhes(result);
      setLoadedDetalhes(true);
    } catch (error) {}
  }, [dispatch, getDebentureDetalhesApi, debentureId]);

  useEffect(() => {
    getDetalhes();
  }, [getDetalhes]);

  return (
    <div style={{ marginRight: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h1">
          {'Debênture-'}
          {detalhes.codigo ?? detalhes.ticket}
        </Typography>
        <DivergenciasButton
          instrumentoFinanceiro={{
            id: detalhes.id,
            codigo: detalhes.codigo ?? detalhes.ticket,
            tipoInstrumentoFinanceiro: 'DEBENTURE',
            origem: detalhes.origem,
            statusValidacao: detalhes.statusValidacao,
            dataDeCriacao: detalhes.dataDeCriacao,
          }}
        />
        {detalhes.suspenso && (
          <Chip
            size="medium"
            label="SUSPENSO"
            color="error"
            style={{
              margin: 20,
            }}
          />
        )}
        {detalhes.hasPagamentosInadimplentes && <Chip size="small" label="INADIMPLENTE" color="error" />}
      </div>
      <LgSpace />
      <HorizontalAppBar>
        <Tabs indicatorColor="primary" value={tabValue} onChange={(_event, newTab) => setTabValue(newTab)}>
          <Tab label="Características da Emissão" value={0} />
          <Tab label="Critérios de Remuneração" value={1} />
          <Tab label="Eventos Financeiros" value={2} />
          <Tab label="Documentos" value={3} />
          <Tab label="Aditamentos" value={7} />
          <Tab label="Suspensões" value={4} />
          <Tab label="Histórico" value={5} />
          <Tab label="KRIs da Operação" value={8} />
          {(isUsuarioControlesInternos || isUsuarioOperacoes) &&
            detalhes &&
            [EmissaoStatus.AguardandoLiquidacao, EmissaoStatus.Emitido, EmissaoStatus.ParcialmenteEmitido].includes(
              detalhes.statusEmissao
            ) && <Tab label="Liquidações" value={6} />}
        </Tabs>
      </HorizontalAppBar>
      {tabValue === 0 && <DetalhesCaracteristicas caracteristicas={detalhes} />}
      {tabValue === 1 && <DetalhesCriteriosRemuneracao criteriosRemuneracao={detalhes} />}
      {tabValue === 2 && <DetalhesEventosFinanceiros eventosFinanceiros={detalhes} />}
      {tabValue === 3 && <DetalhesDocumentos documentos={detalhes} />}
      {tabValue === 4 && (
        <SuspensaoHistorico
          kind={SuspensaoHistoricoKind.InstrumentoFinanceiro}
          registroHistoricos={detalhes.registro.registroHistoricos}
        />
      )}
      {tabValue === 5 && <Historico detalhes={detalhes} />}
      {tabValue === 6 && loadedDetalhes && (
        <Liquidacoes
          onConfirmarLiquidacaoSuccess={getDetalhes}
          liquidacaoDireta={detalhes.criteriosRemuneracao.liquidacaoDireta}
          aguardandoLiquidacao={detalhes.statusEmissao === EmissaoStatus.AguardandoLiquidacao}
          precoUnitarioLiquidacao={detalhes.criteriosRemuneracao.precoUnitarioLiquidacao}
          debentureId={debentureId}
          instrumentoFinanceiroGuid={detalhes.instrumentoFinanceiroGuid}
        />
      )}
      {tabValue === 7 && <DetalhesAditamento documentos={detalhes.instrumentoFinanceiroGuid} />}
      {tabValue === 8 && loadedDetalhes && (
        <KRIsOperacao instrumentoFinanceiroGuid={detalhes.instrumentoFinanceiroGuid} />
      )}
    </div>
  );
};

export default withRouter(DetalhesDebenture);
